package top.wcpe.allincore.ui.runtime.style

import kotlin.jvm.JvmName

/**
 * 视觉 modifier：有序链中的一项，替代 [StyleVisual] 上帝类（ADR-0041）。
 *
 * 每类效果一个子类型——加效果 = 加一个 `sealed` 子类型 + [ModifierDispatcher] 一个 `when` 分支，
 * 不再像 [StyleVisual] 那样改 5 处（开闭原则）。元素只持有它实际用到的 modifier，不再背全套字段。
 *
 * Surface / Shadow / Neon 证明范式后，FR-29-1 补齐 GPU paint 特效 Ripple / Flow / Energy /
 * ProceduralTexture，FR-29-2 补单调用的 [BackdropBlur]（背景毛玻璃）。其余 blur（filter:blur，
 * beginFilterGroup/endFilterGroup 重定向子树到离屏）/ blendMode（pushBlendMode/popBlendMode）/ colorFilter
 * （pushColorMatrix/popColorMatrix）属**作用域型**合成效果——包裹后续绘制、非单图元 paint，与当前逐项分派
 * 模型语义不同，须单独的 scoped-modifier 概念，后续补。corner / border / gradient / mask 不拆、合于 [Surface]。
 */
sealed interface VisualModifier {

    /** 表面：背景填充（[Brush]）+ 圆角 + 边框 + 遮罩（[Mask]），合一（共用 SDF，不硬拆）。 */
    data class Surface(
        @get:JvmName("brush") val brush: Brush,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
        @get:JvmName("borderWidth") val borderWidth: Float = 0f,
        @get:JvmName("borderColor") val borderColor: Int = 0,
        @get:JvmName("mask") val mask: Mask? = null,
    ) : VisualModifier

    /**
     * 阴影：复用既有 [ShadowSpec]（CSS box-shadow 语义）。
     * [corner] 为宿主元素圆角，传给阴影 SDF 使其与圆角对齐（默认 [CornerRadii.ZERO]，直角）。
     */
    data class Shadow(
        @get:JvmName("spec") val spec: ShadowSpec,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /** 霓虹外发光。 */
    data class Neon(
        @get:JvmName("color") val color: Int,
        @get:JvmName("intensity") val intensity: Float,
        @get:JvmName("size") val size: Float,
        @get:JvmName("animated") val animated: Boolean = false,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /**
     * 涟漪扩散环：[centerX]/[centerY] 为圆心相对元素盒左上角的像素坐标，[progress] 取 0~1。
     * [corner] 为宿主圆角，让涟漪被圆角 SDF 裁剪（默认 [CornerRadii.ZERO]）。
     */
    data class Ripple(
        @get:JvmName("color") val color: Int,
        @get:JvmName("progress") val progress: Float,
        @get:JvmName("radius") val radius: Float,
        @get:JvmName("centerX") val centerX: Float,
        @get:JvmName("centerY") val centerY: Float,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /** 流光带：[mode] 取 0 线性 / 1 环形 / 2 对角线，[angleDeg] 仅 mode=0 生效。[corner] 为宿主圆角（SDF 裁剪）。 */
    data class Flow(
        @get:JvmName("color") val color: Int,
        @get:JvmName("speedHz") val speedHz: Float,
        @get:JvmName("widthPx") val widthPx: Float,
        @get:JvmName("angleDeg") val angleDeg: Float,
        @get:JvmName("mode") val mode: Int,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /** 能量场：[pattern] 取 0 脉冲 / 1 波浪 / 2 电弧 / 3 等离子。[corner] 为宿主圆角（SDF 裁剪）。 */
    data class Energy(
        @get:JvmName("color") val color: Int,
        @get:JvmName("intensity") val intensity: Float,
        @get:JvmName("frequency") val frequency: Float,
        @get:JvmName("speed") val speed: Float,
        @get:JvmName("pattern") val pattern: Int,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /**
     * 程序纹理层：[kind] 取 checker/brick/circuit/carbon/brushed/scanline/techgrid/shield，[colorA]/[colorB] 为主副色。
     * [corner] 为宿主圆角（SDF 裁剪）。
     */
    data class ProceduralTexture(
        @get:JvmName("kind") val kind: String,
        @get:JvmName("colorA") val colorA: Int,
        @get:JvmName("colorB") val colorB: Int,
        @get:JvmName("scale") val scale: Float,
        @get:JvmName("density") val density: Float,
        @get:JvmName("intensity") val intensity: Float,
        @get:JvmName("corner") val corner: CornerRadii = CornerRadii.ZERO,
    ) : VisualModifier

    /** 背景毛玻璃：采样元素背后已落屏的像素做高斯模糊后画到元素之下（单调用，非作用域型）。[blurRadius] 为模糊半径（像素）。 */
    data class BackdropBlur(@get:JvmName("blurRadius") val blurRadius: Float) : VisualModifier

    /** FR-UI-020 程序化材质层：字段只承载平台中立契约，具体 shader/FBO 由平台实现解释。 */
    sealed interface MaterialLayer : VisualModifier {
        val kind: String
        val opacity: Float
        val blend: String
    }

    /** 资源纹理采样层。 */
    data class TextureLayer(
        @get:JvmName("source") val source: String,
        @get:JvmName("sampling") val sampling: String = "stretch",
        @get:JvmName("uv") val uv: String? = null,
        @get:JvmName("scale") val scale: Float = 1f,
        @get:JvmName("tint") val tint: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
        @get:JvmName("nineSlice") val nineSlice: String? = null,
    ) : MaterialLayer {
        override val kind: String get() = "textureLayer"
    }

    /** 内置程序材质覆盖层。 */
    data class MaterialOverlay(
        @get:JvmName("material") val material: String,
        @get:JvmName("seed") val seed: Int = 0,
        @get:JvmName("scale") val scale: Float = 1f,
        @get:JvmName("roughness") val roughness: Float = 0f,
        @get:JvmName("tint") val tint: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
    ) : MaterialLayer {
        override val kind: String get() = "materialOverlay"
    }

    /** 局部历史采样层。 */
    data class HistoryLayer(
        @get:JvmName("source") val source: String,
        @get:JvmName("frames") val frames: Int = 1,
        @get:JvmName("downsample") val downsample: Int = 1,
        @get:JvmName("decay") val decay: Float = 0f,
        @get:JvmName("blur") val blur: Float = 0f,
        @get:JvmName("distortion") val distortion: Float = 0f,
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
        @get:JvmName("trigger") val trigger: String? = null,
    ) : MaterialLayer {
        override val kind: String get() = "historyLayer"
    }

    /** 磨损边框材质层。 */
    data class WornFrame(
        @get:JvmName("thickness") val thickness: Float = 1f,
        @get:JvmName("erosion") val erosion: Float = 0f,
        @get:JvmName("roughness") val roughness: Float = 0f,
        @get:JvmName("edgeStrength") val edgeStrength: Float = 1f,
        @get:JvmName("tint") val tint: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
    ) : MaterialLayer {
        override val kind: String get() = "wornFrame"
    }

    /** 符文覆盖层。 */
    data class RuneOverlay(
        @get:JvmName("runeSet") val runeSet: String,
        @get:JvmName("seed") val seed: Int = 0,
        @get:JvmName("glow") val glow: Float = 0f,
        @get:JvmName("density") val density: Float = 1f,
        @get:JvmName("tint") val tint: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
    ) : MaterialLayer {
        override val kind: String get() = "runeOverlay"
    }

    /** 受限材质图入口。 */
    data class MaterialGraph(
        @get:JvmName("graph") val graph: String,
        @get:JvmName("budget") val budget: Int = 0,
        @get:JvmName("fallback") val fallback: String? = null,
    ) : MaterialLayer {
        override val kind: String get() = "materialGraph"
        override val opacity: Float get() = 1f
        override val blend: String get() = "normal"
    }

    /** 资源包 shader 层入口。 */
    data class ShaderLayer(
        @get:JvmName("program") val program: String,
        @get:JvmName("params") val params: Map<String, Any?> = emptyMap(),
        @get:JvmName("opacity") override val opacity: Float = 1f,
        @get:JvmName("blend") override val blend: String = "normal",
        @get:JvmName("fallback") val fallback: String? = null,
    ) : MaterialLayer {
        override val kind: String get() = "shaderLayer"
    }

    /**
     * FR-UI-031 粒子层（ADR-0071）：挂在元素上的持续 / burst 粒子发射器，参与矩形边界碰撞与鼠标 hover 扰动。
     *
     * 与 [MaterialLayer] 不同——它不是一次性着色，而是有状态、逐帧演化的发射器；也不做作用域 begin/end 包裹，
     * 而是被 [Style.modifiers] 分出到 [VisualModifierChain.particleLayers] 独立段，由 UIRenderer 按 [layer]
     * 在「元素表面之后子节点之前（back）」或「子节点之后（front）」各发一条延迟绘制命令。
     *
     * 字段只承载平台中立契约：物理 / 着色由中立 `ParticleSystem` 解释、GPU 实例化由平台绘制。
     *
     * @property kind 效果族：`sparks`（火星上升）/ `snow`（飘雪下落）/ `runeflux`（符文环绕流光）。
     * @property emission 发射模式：`continuous`（按 [rate] 持续）/ `burst`（由 [trigger] 上升沿一次发 [count] 个）。
     * @property layer 层次：`front`（前景，默认）/ `back`（背景，在自身表面之后子节点之前）。
     * @property rate continuous 发射速率（个 / 秒）。
     * @property count burst 单次发射数量。
     * @property trigger burst 触发的交互态 / 事件名（如 `hovered` / `pressed`）；continuous 忽略。
     * @property lifetime 单粒子寿命（秒）。
     * @property gravity 竖直加速度（像素 / 秒²，正为向下）。
     * @property wind 水平加速度（像素 / 秒²，正为向右）。
     * @property speed 初始发射速度（像素 / 秒）。
     * @property size 粒子基础边长（像素）。
     * @property colorA 主色 ARGB（发射初期）。
     * @property colorB 副色 ARGB（寿命末期，用于渐变淡出）。
     * @property glow 加色发光强度（0~1）。
     * @property bounce 矩形边界碰撞回弹系数（0 完全吸收 / 1 完全弹回）。
     * @property hoverForce 鼠标光标斥力强度（0 关闭）。
     * @property seed 随机种子，保证可复现。
     * @property emitShape FR-UI-032 发射形状（point / area / edge / border / ring / line）；null 沿用 kind 默认形状。
     * @property emitEdge emitShape=edge 时的边（top / bottom / left / right）。
     * @property emitAngle 发射基准方向（度，0=向右、90=向下）；NaN 未声明（edge/border/ring/line 沿法线）。
     * @property emitSpread 扩散角（度，围绕基准方向 ± 一半）。
     * @property emitInward 法线方向翻转为朝内。
     * @property noiseStrength 噪声湍流强度（像素 / 秒²，0 关闭）。
     * @property noiseFrequency 噪声湍流频率。
     * @property attractX 吸引锚点 x（元素相对坐标 0~1）。
     * @property attractY 吸引锚点 y（元素相对坐标 0~1）。
     * @property attractStrength 吸引强度（正=吸引、负=排斥、0 关闭）。
     * @property drag 速度阻尼系数（1/秒，0 关闭）。
     * @property colorOverLifetime FR-UI-033 颜色停靠点曲线原始表 `[[t, color], ...]`；声明后取代 colorA/colorB。
     * @property sizeOverLifetime 尺寸倍率停靠点曲线原始表 `[[t, factor], ...]`。
     * @property alphaOverLifetime 不透明度停靠点曲线原始表；声明后取代内置淡入淡出包络。
     * @property rotationOverLifetime 旋转停靠点曲线原始表（度）。
     * @property texture FR-UI-034 sprite 纹理资源路径；空 / null 为程序化柔光圆点。
     * @property sheetRows 序列帧行数（≥1）。
     * @property sheetCols 序列帧列数（≥1）。
     * @property sheetFps 序列帧速率（帧 / 秒，≤0 关闭序列帧）。
     * @property trailLength 拖尾历史点数（0 关闭）。
     * @property trailFade 拖尾整体强度（≤0 显式关闭，默认 1）。
     */
    data class ParticleLayer(
        @get:JvmName("kind") val kind: String,
        @get:JvmName("emission") val emission: String = "continuous",
        @get:JvmName("layer") val layer: String = "front",
        @get:JvmName("rate") val rate: Float = 20f,
        @get:JvmName("count") val count: Int = 24,
        @get:JvmName("trigger") val trigger: String? = null,
        @get:JvmName("lifetime") val lifetime: Float = 2f,
        @get:JvmName("gravity") val gravity: Float = 40f,
        @get:JvmName("wind") val wind: Float = 0f,
        @get:JvmName("speed") val speed: Float = 30f,
        @get:JvmName("size") val size: Float = 3f,
        @get:JvmName("colorA") val colorA: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("colorB") val colorB: Int = 0xFFFFFFFF.toInt(),
        @get:JvmName("glow") val glow: Float = 0.6f,
        @get:JvmName("bounce") val bounce: Float = 0.3f,
        @get:JvmName("hoverForce") val hoverForce: Float = 0f,
        @get:JvmName("opacity") val opacity: Float = 1f,
        @get:JvmName("blend") val blend: String = "additive",
        @get:JvmName("seed") val seed: Int = 0,
        @get:JvmName("emitShape") val emitShape: String? = null,
        @get:JvmName("emitEdge") val emitEdge: String = "top",
        @get:JvmName("emitAngle") val emitAngle: Float = Float.NaN,
        @get:JvmName("emitSpread") val emitSpread: Float = 30f,
        @get:JvmName("emitInward") val emitInward: Boolean = false,
        @get:JvmName("noiseStrength") val noiseStrength: Float = 0f,
        @get:JvmName("noiseFrequency") val noiseFrequency: Float = 1f,
        @get:JvmName("attractX") val attractX: Float = 0.5f,
        @get:JvmName("attractY") val attractY: Float = 0.5f,
        @get:JvmName("attractStrength") val attractStrength: Float = 0f,
        @get:JvmName("drag") val drag: Float = 0f,
        @get:JvmName("colorOverLifetime") val colorOverLifetime: List<*>? = null,
        @get:JvmName("sizeOverLifetime") val sizeOverLifetime: List<*>? = null,
        @get:JvmName("alphaOverLifetime") val alphaOverLifetime: List<*>? = null,
        @get:JvmName("rotationOverLifetime") val rotationOverLifetime: List<*>? = null,
        @get:JvmName("texture") val texture: String? = null,
        @get:JvmName("sheetRows") val sheetRows: Int = 1,
        @get:JvmName("sheetCols") val sheetCols: Int = 1,
        @get:JvmName("sheetFps") val sheetFps: Float = 0f,
        @get:JvmName("trailLength") val trailLength: Int = 0,
        @get:JvmName("trailFade") val trailFade: Float = 1f,
    ) : VisualModifier
}
